import React from "react";
import { PDFViewer } from "@react-pdf/renderer";
import { PDFType, PDFContext } from "./types";
import { usePDFGeneration } from "./utils";
import { CareerPlanPDFWithSvgConversion } from "./CareerPlanPDFWithSvgConversion";

interface PDFPreviewerProps {
  type: PDFType;
  data: any;
  cmsData?: PDFContext["cmsData"];
  customConfig?: Partial<PDFContext["config"]>;
  metadata?: PDFContext["metadata"];
  width?: string | number;
  height?: string | number;
  showToolbar?: boolean;
  className?: string;
}

export const PDFPreviewer: React.FC<PDFPreviewerProps> = ({
  type,
  data,
  cmsData,
  customConfig,
  metadata,
  width = "100%",
  height = "100vh",
  showToolbar = true,
  className,
}) => {
  const { generatePDF, isPDFTypeSupported } = usePDFGeneration();

  if (!isPDFTypeSupported(type)) {
    return (
      <div style={{ padding: "20px", color: "red" }}>
        PDF type {type} is not supported
      </div>
    );
  }

  const pdfData = generatePDF(type, data, customConfig, metadata, cmsData);

  if (!pdfData) {
    return (
      <div style={{ padding: "20px", color: "red" }}>
        Failed to generate PDF
      </div>
    );
  }

  const { component } = pdfData;

  return (
    <PDFViewer
      width={width}
      height={height}
      showToolbar={showToolbar}
      className={className}
    >
      {component as any}
    </PDFViewer>
  );
};

// Specialized component for Career Plan preview
export const CareerPlanPDFPreviewer: React.FC<{
  careerPlanData: any;
  cmsData?: PDFContext["cmsData"];
  width?: string | number;
  height?: string | number;
  showToolbar?: boolean;
  className?: string;
}> = ({ careerPlanData, cmsData, ...props }) => {
  return (
    <PDFPreviewer
      type={PDFType.CAREER_PLAN}
      data={careerPlanData}
      cmsData={cmsData}
      customConfig={{
        fileName: `career_plan_${careerPlanData.user?.name?.replace(/\s+/g, "_") || "student"}.pdf`,
      }}
      {...props}
    />
  );
};
